import axiosInstance from "../axiosInstance";
import { appendWarehouseIds } from "../analyticsWarehouses";

/**
 * Same Order Source serialization as Sales Analysis / Order Management:
 * `source_channel[$in][]=...`, coalesced server-side with `oms_provider`.
 * An empty list means all channels and appends nothing.
 */
const appendSourceChannels = (params, sourceChannels) => {
  (sourceChannels || [])
    .filter((value) => value != null && value !== "")
    .forEach((value) => params.append("source_channel[$in][]", value));
};

export const salesHistoryService = {
  /**
   * GET /api/metrics/sales-history
   * Server resolves client from JWT when present; client_id required for admins.
   * warehouseIds: [] = All Warehouses; one id → `warehouse_id=`, several →
   * `warehouse_id[$in][]=` (one attributed scope server-side).
   */
  fetch: async ({
    clientId,
    startDate,
    endDate,
    warehouseIds = [],
    sku,
    productId,
    skip,
    limit,
    sortField,
    sortDir,
    sourceChannels = [],
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    appendWarehouseIds(params, warehouseIds);
    if (sku) params.set("sku", sku);
    if (productId) params.set("product_id", productId);
    if (skip != null) params.set("$skip", String(skip));
    if (limit != null) params.set("$limit", String(limit));
    if (sortField) params.set("sort_field", sortField);
    if (sortDir) params.set("sort_dir", sortDir);
    appendSourceChannels(params, sourceChannels);
    const response = await axiosInstance.get(
      `/metrics/sales-history?${params.toString()}`,
    );
    return response.data;
  },

  searchCatalog: async ({
    clientId,
    q,
    skip = 0,
    limit = 15,
  } = {}) => {
    const params = new URLSearchParams();
    params.set("catalog_search", "1");
    if (clientId) params.set("client_id", clientId);
    if (q) params.set("q", q);
    params.set("$skip", String(skip));
    params.set("$limit", String(limit));
    const response = await axiosInstance.get(
      `/metrics/sales-history?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * GET /api/metrics/sales-history-detail
   * Lazy SKU detail. Called only after a row click — not during table load.
   */
  fetchDetail: async ({
    clientId,
    sku,
    productId,
    startDate,
    endDate,
    warehouseIds = [],
    sourceChannels = [],
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    // Product mode analyses the product's child SKUs; `product_id` wins.
    if (productId) params.set("product_id", productId);
    else if (sku) params.set("sku", sku);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    appendWarehouseIds(params, warehouseIds);
    appendSourceChannels(params, sourceChannels);
    const response = await axiosInstance.get(
      `/metrics/sales-history-detail?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * GET /api/metrics/sales-history-daily
   * Sales History V2.5. Called only when a month bar is clicked inside the
   * already open SKU detail modal — never during table or detail load.
   */
  fetchDailyAnalysis: async ({
    clientId,
    sku,
    productId,
    month,
    warehouseIds = [],
    sourceChannels = [],
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (productId) params.set("product_id", productId);
    else if (sku) params.set("sku", sku);
    if (month) params.set("month", month);
    appendWarehouseIds(params, warehouseIds);
    appendSourceChannels(params, sourceChannels);
    const response = await axiosInstance.get(
      `/metrics/sales-history-daily?${params.toString()}`,
    );
    return response.data;
  },
};
